import type { MediaRef } from "@rhema/client";

/** The name a file is saved under: its own name, or "Rhema photo.jpg" when it came without one. */
export function fileNameOf(media: Pick<MediaRef, "name" | "mime">): string {
  if (media.name) return media.name;
  const ext =
    media.mime.split("/")[1]?.split(";")[0]?.replace("jpeg", "jpg").replace("quicktime", "mov") || "bin";
  const noun = media.mime.startsWith("video/") ? "video" : media.mime.startsWith("image/") ? "photo" : "file";
  return `Rhema ${noun}.${ext}`;
}

export type OpenAs = "photo" | "video" | "audio" | "pdf" | "text";

const PHOTO = new Set(["image/jpeg", "image/png", "image/gif", "image/webp", "image/avif"]);
const VIDEO = new Set(["video/mp4", "video/webm", "video/quicktime"]);
const TEXT = new Set(["text/plain", "text/csv", "text/markdown"]);

/**
 * How Rhema can show a file inside the app, or null when it can only be saved. The type is the
 * sender's word for it, so it is only trusted to pick a viewer that cannot run what it shows.
 */
export function openAs(mime: string): OpenAs | null {
  const type = mime.split(";")[0]?.trim().toLowerCase() ?? "";
  if (PHOTO.has(type)) return "photo";
  if (VIDEO.has(type)) return "video";
  if (type.startsWith("audio/")) return "audio";
  if (type === "application/pdf") return "pdf";
  if (TEXT.has(type)) return "text";
  return null;
}

/** Text files larger than this are saved, not shown. */
export const TEXT_SHOWN_BYTES = 2 * 1024 * 1024;
